<template>
  <section class="register-study">
    <div class="layout">
      <StudyFormClinician
        :initialStudy="editingStudy"
        :isLoading="isSubmitting"
        @submit="handleSubmit"
        @cancel="resetEditing"
      />
      <StudiesTable
        :studies="getPageItems"
        :isLoading="isLoading"
        :paginationOptions="paginationOptions"
        @onPagination="handlePagination"
        @edit="startEditing"
        @delete="confirmDelete"
        @edit-active-inactive="toggleActive"
      />
    </div>

    <ModalConfirm
      v-model:visible="showDeleteModal"
      title="Deactivate study?"
      @confirm="executeDelete"
    />
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useStudiesClinician } from '@/studies/composables/useStudiesClinician';
import StudyFormClinician from '@/studies/organisms/StudyFormClinician.vue';
import StudiesTable from '@/studies/organisms/StudiesTableClinician.vue';
import ModalConfirm from '@/components/ModalConfirm.vue';
import { toast } from 'vue3-toastify';
import { useOptions } from '@/components/common/paginationOptions';
import { getStudies } from '../services/studies';

const { studies, isLoading, fetchStudies, createStudy, editStudy, removeStudy, toggleActive } =
  useStudiesClinician();

const { handlePagination, paginationOptions, getPageItems } = useOptions(studies);

const editingStudy = ref(null);
const isSubmitting = ref(false);

const showDeleteModal = ref(false);
const toDeleteId = ref(null);

onMounted(fetchStudies);

function startEditing(study) {
  editingStudy.value = study;
}

function resetEditing() {
  editingStudy.value = null;
}

const validateIfTheStudyAlreadyExists = async (study) => {
  const studiesGeneral = await getStudies();
  const studiesClinician = studies.value;
  const existingInStudiesClinician = studiesClinician?.find(
    (s) => s.identifier === study.identifier
  );
  const existingInStudiesGeneral = studiesGeneral?.find((s) => s.idNumber === study.identifier);

  return existingInStudiesClinician || existingInStudiesGeneral;
};

async function handleSubmit(formData) {
  isSubmitting.value = true;
  try {
    if (editingStudy.value) {
      await editStudy({
        id: editingStudy.value.id,
        name: formData.name,
        identifier: formData.identifier,
        enrollmentPassword: formData.studyEnrollmentPassword,
        version: editingStudy.value.version + 1,
        isActive: editingStudy.value.isActive,
      });
      toast.success('Study updated successfully');
    } else {
      const existingStudy = await validateIfTheStudyAlreadyExists(formData);

      if (existingStudy) {
        toast.error('Study already exists with this identifier (General or Clinician study)');
        return;
      }
      await createStudy({
        name: formData.name,
        identifier: formData.identifier,
        enrollmentPassword: formData.studyEnrollmentPassword,
        version: 1,
      });
      toast.success('Study created successfully');
    }
    resetEditing();
  } catch (error) {
    toast.error(error.message || 'An error occurred');
  } finally {
    isSubmitting.value = false;
  }
}

function confirmDelete(study) {
  toDeleteId.value = study.id;
  showDeleteModal.value = true;
}

async function executeDelete() {
  try {
    await removeStudy(toDeleteId.value);
    toast.success('Study deactivated');
  } catch (error) {
    toast.error(error.message || 'Delete failed');
  } finally {
    showDeleteModal.value = false;
  }
}
</script>

<style scoped lang="scss">
.register-study {
  padding: 2rem;
}
</style>
